import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import { dueDiligenceChatActionAtom } from "../../../Common/State/dueDiligenceChatAction";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { NewCaseModal } from "../NewCase/Modal";
import { useNewCaseForm } from "../NewCase/useNewCaseForm";
import {
  CasesListWrapper,
  CasesListContent,
  CasesListTitleSection,
  CasesListTitleInner,
  CasesListPageTitle,
  CasesListPageSubtitle,
  CasesListHeaderButton,
  CasesListHeaderActions,
  SearchAndFiltersWrapper,
  SearchInputWrapper,
  SearchInput,
  FiltersContainer,
  FilterButton,
  TableWrapper,
  Table,
  TableHead,
  TableHeadRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  NameText,
  StatusBadge,
  PaginationWrapper,
  PaginationInfo,
  PaginationControls,
  RowsPerPage,
  PaginationButtons,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
} from "./Styles";
import { Button } from "../../../Components/Button";
import { DueDiligenceCase, DueDiligenceTier, getDueDiligenceCaseStatusLabel } from "../../../Common/Types-DTOs/DueDiligence";
import { MdRemoveRedEye } from "react-icons/md";
import { AiOutlineFileSearch } from "react-icons/ai";
import { FiSearch, FiChevronLeft, FiChevronRight, FiChevronDown, FiCalendar, FiCheck, FiChevronUp, FiArchive, FiRefreshCw } from "react-icons/fi";
import { useDueDiligence } from "../../../Common/Hooks/useDueDiligence";
import { isErr } from "../../../Common/Utils/Result";
import { useBusiness } from "../../../Common/Hooks/useBusiness";
import { DueDiligenceCreditCosts } from "../NewCase/PaymentSection";
import { CreditBalance } from "./CreditBalance";
import { formatAbbreviatedDate } from "../../../Common/Utils/dateUtils";
import { DUE_DILIGENCE_TIER_LEVELS } from "../../../Common/Utils/dueDiligencePlans";
import { eventBus } from "../../../Common/EventBus";
import { Notification } from "../../../Components/Notifications";
import { Popup } from "../../../Components/Popup";
import { Modal } from "../../../Components/Modal";
import { UnarchiveConfirmModal } from "../Components/UnarchiveConfirmModal";

type FilterType = "name" | "alias" | "status" | "date" | null;
type SortField = "name" | "tier" | "createdAt" | "status" | null;

const TIER_ORDER: Record<DueDiligenceTier, number> = { basic: 1, professional: 2, premium: 3 };
type SortDirection = "asc" | "desc";

export const CasesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cases, setCases] = useState<DueDiligenceCase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterType>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: "", end: "" });
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [statusDropdownPosition, setStatusDropdownPosition] = useState({ top: 0, left: 0 });
  const statusButtonRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showNewCaseModal, setShowNewCaseModal] = useState(false);
  const [showArchiveConfirmModal, setShowArchiveConfirmModal] = useState(false);
  const [showUnarchiveConfirmModal, setShowUnarchiveConfirmModal] = useState(false);
  const [caseToArchive, setCaseToArchive] = useState<{ id: string; name: string } | null>(null);
  const [caseToUnarchive, setCaseToUnarchive] = useState<{ id: string; name: string; hasFinalStatement: boolean; previousStatus?: string | null } | null>(null);

  const newCaseForm = useNewCaseForm();
  const [dueDiligenceChatAction] = useRecoilState(dueDiligenceChatActionAtom);
  const processedDDChatIdRef = useRef<string | null | undefined>(null);
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { listCases, updateCase, getDueDiligencePricing } = useDueDiligence();
  const { business } = useBusiness();
  const hasDueDiligencePlan = Boolean(business?.credits?.dueDiligencePlan);
  const [creditCosts, setCreditCosts] = useState<DueDiligenceCreditCosts | null>(null);

  // Only to warn when the balance runs short: without them the balance shows anyway
  useEffect(() => {
    if (!hasDueDiligencePlan) return;
    getDueDiligencePricing().then((result) => {
      if (!isErr(result)) setCreditCosts(result.value.creditCosts);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDueDiligencePlan]);

  // Deduplicate simultaneous requests (e.g. from StrictMode)



  // Track if we need to fetch archived cases
  const includeArchived = useMemo(() => selectedStatuses.includes('archived'), [selectedStatuses]);

  const loadCases = async (fetchArchived: boolean = false) => {
    setIsLoading(true);
    try {
      // Cancel previous request if running? (Not easily possible with Promises, but we use ref to track specific promise)
      // Actually, we should just let it finish.

      const result = await listCases({ includeArchived: fetchArchived });

      if (isErr(result)) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification(
            "error",
            t("dueDiligence.casesList.errors.loadCases")
          )
        );
        console.error("Error loading cases:", result.error);
        return;
      }

      setCases(result.value.cases);
    } catch (error) {
      console.error("Error loading cases:", error);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          t("dueDiligence.casesList.errors.loadCases")
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCases(includeArchived);
  }, [includeArchived, t]);

  // Auto-open modal pre-filled when coming from chatbot DD action
  useEffect(() => {
    if (!dueDiligenceChatAction) return;
    if (processedDDChatIdRef.current === dueDiligenceChatAction.chatId) return;
    processedDDChatIdRef.current = dueDiligenceChatAction.chatId;

    const { data } = dueDiligenceChatAction;
    if (data.name) newCaseForm.handleInputChange("name", data.name);
    if (data.address) newCaseForm.handleInputChange("propertyAddress", data.address);
    if (data.cadastralReference) newCaseForm.handleInputChange("cadastralReference", data.cadastralReference);
    if (data.propertyType) newCaseForm.handleInputChange("propertyType", data.propertyType);

    const hasPreFilledData = !!(data.name || data.address || data.cadastralReference || data.propertyType);
    if (hasPreFilledData) setShowNewCaseModal(true);
  }, [dueDiligenceChatAction]);

  const refreshCases = async () => {
    await loadCases();
  };

  const updateStatusDropdownPosition = useCallback(() => {
    if (statusButtonRef.current) {
      const rect = statusButtonRef.current.getBoundingClientRect();
      setStatusDropdownPosition({
        top: rect.bottom + 8,
        left: rect.right - 280, // 280px is minWidth of dropdown
      });
    }
  }, []);

  useEffect(() => {
    if (showStatusDropdown) {
      updateStatusDropdownPosition();
    }
  }, [showStatusDropdown, updateStatusDropdownPosition]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check for status dropdown (portal)
      const clickedInsideStatusButton = statusButtonRef.current?.contains(target);
      const clickedInsideStatusDropdown = statusDropdownRef.current?.contains(target);

      if (showStatusDropdown && !clickedInsideStatusButton && !clickedInsideStatusDropdown) {
        setShowStatusDropdown(false);
      }

      // Check for date picker (still uses data attribute)
      const targetElement = target as HTMLElement;
      if (showDatePicker && !targetElement.closest('[data-filter-dropdown]')) {
        setShowDatePicker(false);
      }
    };

    const handleScroll = () => {
      if (showStatusDropdown) {
        setShowStatusDropdown(false);
      }
    };

    if (showStatusDropdown || showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [showStatusDropdown, showDatePicker]);

  const getStatusLabel = getDueDiligenceCaseStatusLabel;

  const formatDate = formatAbbreviatedDate;


  // Filter cases based on search query, active filter, status, and date
  const filteredCases = useMemo(() => {
    let filtered = [...cases];

    // Apply search filter (for name/alias when those filters are active)
    if (searchQuery.trim() && (activeFilter === "name" || activeFilter === "alias")) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((caseItem) => {
        if (activeFilter === "name") {
          return caseItem.name.toLowerCase().includes(query);
        }
        if (activeFilter === "alias") {
          return caseItem.alias.toLowerCase().includes(query);
        }
        return false;
      });
    } else if (searchQuery.trim() && activeFilter === null) {
      // General search when no specific filter is active
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((caseItem) => {
        if (caseItem.name.toLowerCase().includes(query)) return true;
        if (caseItem.alias.toLowerCase().includes(query)) return true;
        if (getStatusLabel(caseItem.status).toLowerCase().includes(query)) return true;
        if (formatDate(caseItem.createdAt).toLowerCase().includes(query)) return true;
        if (caseItem.propertyAddress && caseItem.propertyAddress.toLowerCase().includes(query)) return true;
        return false;
      });
    }

    // Apply status filter
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((caseItem) =>
        selectedStatuses.includes(caseItem.status)
      );
    }

    // Apply date range filter
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter((caseItem) => {
        const caseDate = new Date(caseItem.createdAt);
        caseDate.setHours(0, 0, 0, 0);

        if (dateRange.start && dateRange.end) {
          const startDate = new Date(dateRange.start);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(dateRange.end);
          endDate.setHours(23, 59, 59, 999);
          return caseDate >= startDate && caseDate <= endDate;
        } else if (dateRange.start) {
          const startDate = new Date(dateRange.start);
          startDate.setHours(0, 0, 0, 0);
          return caseDate >= startDate;
        } else if (dateRange.end) {
          const endDate = new Date(dateRange.end);
          endDate.setHours(23, 59, 59, 999);
          return caseDate <= endDate;
        }
        return true;
      });
    }

    // Apply sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue: string | number;
        let bValue: string | number;

        switch (sortField) {
          case "name":
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
            break;
          case "tier":
            aValue = a.tier ? TIER_ORDER[a.tier] : 0;
            bValue = b.tier ? TIER_ORDER[b.tier] : 0;
            break;
          case "createdAt":
            aValue = new Date(a.createdAt).getTime();
            bValue = new Date(b.createdAt).getTime();
            break;
          case "status":
            aValue = getStatusLabel(a.status).toLowerCase();
            bValue = getStatusLabel(b.status).toLowerCase();
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [cases, searchQuery, activeFilter, selectedStatuses, dateRange, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredCases.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedCases = filteredCases.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleFilterClick = (filterType: FilterType) => {
    // Close other filter dropdowns
    setShowStatusDropdown(false);
    setShowDatePicker(false);

    if (activeFilter === filterType) {
      setActiveFilter(null);
      if (filterType === "status") {
        setSelectedStatuses([]);
      }
      if (filterType === "date") {
        setDateRange({ start: "", end: "" });
      }
    } else {
      setActiveFilter(filterType);
      if (filterType === "status") {
        setShowStatusDropdown(true);
      }
      if (filterType === "date") {
        setShowDatePicker(true);
      }
    }
    setCurrentPage(1);
  };

  const handleStatusToggle = (status: string) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(status)) {
        return prev.filter((s) => s !== status);
      } else {
        return [...prev, status];
      }
    });
    setCurrentPage(1);
  };

  const handleDateRangeChange = (field: "start" | "end", value: string) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
    setCurrentPage(1);
  };

  const clearDateRange = () => {
    setDateRange({ start: "", end: "" });
    setCurrentPage(1);
  };

  const getSearchPlaceholder = () => {
    if (activeFilter === "name") {
      return t("dueDiligence.casesList.search.placeholderName");
    }
    if (activeFilter === "alias") {
      return t("dueDiligence.casesList.search.placeholderAlias");
    }
    return t("dueDiligence.casesList.search.placeholderDefault");
  };

  const statusOptions = [
    { value: "draft", label: t("dueDiligence.casesList.statusOptions.draft"), color: "#9CA3AF" },
    { value: "pending_payment", label: t("dueDiligence.casesList.statusOptions.pendingPayment"), color: "#F59E0B" },
    { value: "in_progress", label: t("dueDiligence.casesList.statusOptions.inProgress"), color: "#3B82F6" },
    { value: "under_review", label: t("dueDiligence.casesList.statusOptions.underReview"), color: "#3B82F6" },
    { value: "completed", label: t("dueDiligence.casesList.statusOptions.completed"), color: "#10B981" },
    { value: "archived", label: t("dueDiligence.casesList.statusOptions.archived"), color: "#F59E0B" },
  ];


  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new field with default descending order
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const handleArchiveClick = (caseId: string, caseName: string) => {
    setCaseToArchive({ id: caseId, name: caseName });
    setShowArchiveConfirmModal(true);
  };

  const handleConfirmArchive = async () => {
    if (!caseToArchive) return;

    try {
      const result = await updateCase(caseToArchive.id, { status: "archived" });

      if (isErr(result)) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification(
            "error",
            t("dueDiligence.casesList.errors.archiveCase")
          )
        );
        return;
      }

      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "success",
          t("dueDiligence.casesList.notifications.archiveSuccess", { caseName: caseToArchive.name })
        )
      );

      await refreshCases();
    } catch (error) {
      console.error("Error archiving case:", error);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          t("dueDiligence.casesList.errors.archiveCase")
        )
      );
    } finally {
      setShowArchiveConfirmModal(false);
      setCaseToArchive(null);
    }
  };

  const handleUnarchiveClick = (caseId: string, caseName: string, hasFinalStatement: boolean, previousStatus?: string | null) => {
    setCaseToUnarchive({ id: caseId, name: caseName, hasFinalStatement, previousStatus });
    setShowUnarchiveConfirmModal(true);
  };

  const handleConfirmUnarchive = async () => {
    if (!caseToUnarchive) return;

    try {
      // Logic for restoring status:
      // 1. If previousStatus exists, use it.
      // 2. Fallback: If hasFinalStatement -> completed, else -> under_review (or in_progress?)
      //    'under_review' is safer default if no previous status.
      let newStatus: string;
      if (caseToUnarchive.previousStatus) {
         newStatus = caseToUnarchive.previousStatus;
      } else {
         newStatus = caseToUnarchive.hasFinalStatement ? "completed" : "under_review";
      }

      const result = await updateCase(caseToUnarchive.id, { status: newStatus as any });

      if (isErr(result)) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification(
            "error",
            t("dueDiligence.casesList.errors.unarchiveCase")
          )
        );
        return;
      }

      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "success",
          t("dueDiligence.casesList.notifications.unarchiveSuccess", {
            caseName: caseToUnarchive.name,
            status: getDueDiligenceCaseStatusLabel(newStatus),
          })
        )
      );

      await refreshCases();
    } catch (error) {
      console.error("Error unarchiving case:", error);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          t("dueDiligence.casesList.errors.unarchiveCase")
        )
      );
    } finally {
      setShowUnarchiveConfirmModal(false);
      setCaseToUnarchive(null);
    }
  };

  return (
    <CasesListWrapper>
      <CasesListTitleSection>
        <CasesListTitleInner>
          <CasesListPageTitle>
            {t("dueDiligence.casesList.pageTitle")}
          </CasesListPageTitle>
          <CasesListPageSubtitle>
            {t("dueDiligence.casesList.pageSubtitle")}
          </CasesListPageSubtitle>
        </CasesListTitleInner>
        <CasesListHeaderActions>
          {business?.credits && (
            <CreditBalance
              credits={business.credits}
              creditCosts={creditCosts}
              onChangePlan={() => navigate("/due-diligence-plans")}
            />
          )}
          <CasesListHeaderButton>
            <Button onClick={() => setShowNewCaseModal(true)}>
              <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>+</span>
              {t("dueDiligence.casesList.newCaseButton")}
            </Button>
          </CasesListHeaderButton>
        </CasesListHeaderActions>
      </CasesListTitleSection>

      <CasesListContent>
        {isLoading ? (
          <EmptyState>
            <p>{t("dueDiligence.casesList.loading")}</p>
          </EmptyState>
        ) : cases.length > 0 ? (
          <>
            <SearchAndFiltersWrapper>
              <SearchInputWrapper>
                <FiSearch className="search-icon" size={20} />
                <SearchInput
                  type="text"
                  placeholder={getSearchPlaceholder()}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  disabled={activeFilter === "status" || activeFilter === "date"}
                />
              </SearchInputWrapper>
              <FiltersContainer>
                <span className="filter-label">{t("dueDiligence.casesList.filters.label")}</span>
                <div style={{ position: 'relative' }}>
                  <FilterButton
                    $active={activeFilter === "name"}
                    onClick={() => handleFilterClick("name")}
                  >
                    {t("dueDiligence.casesList.filters.name")}
                  </FilterButton>
                </div>
                <div style={{ position: 'relative' }}>
                  <FilterButton
                    $active={activeFilter === "alias"}
                    onClick={() => handleFilterClick("alias")}
                  >
                    {t("dueDiligence.casesList.filters.alias")}
                  </FilterButton>
                </div>
                <div ref={statusButtonRef} style={{ position: 'relative' }}>
                  <FilterButton
                    $active={activeFilter === "status" || selectedStatuses.length > 0}
                    onClick={() => handleFilterClick("status")}
                  >
                    {t("dueDiligence.casesList.filters.status")}
                    {selectedStatuses.length > 0 && (
                      <span style={{
                        marginLeft: '0.25rem',
                        fontSize: '0.75rem',
                        backgroundColor: '#021526',
                        color: '#ffffff',
                        borderRadius: '50%',
                        width: '1.25rem',
                        height: '1.25rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {selectedStatuses.length}
                      </span>
                    )}
                    {showStatusDropdown ? (
                      <FiChevronUp size={14} style={{ marginLeft: '0.25rem' }} />
                    ) : (
                      <FiChevronDown size={14} style={{ marginLeft: '0.25rem' }} />
                    )}
                  </FilterButton>
                  {showStatusDropdown && createPortal(
                    <div
                      ref={statusDropdownRef}
                      style={{
                        position: 'fixed',
                        top: statusDropdownPosition.top,
                        left: statusDropdownPosition.left,
                        backgroundColor: '#ffffff',
                        border: '1px solid #E5E7EB',
                        borderRadius: '0.75rem',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        padding: '1rem',
                        zIndex: 9999,
                        minWidth: '280px'
                      }}
                    >
                      <div style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#111827',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '1rem',
                        paddingBottom: '0.75rem',
                        borderBottom: '1px solid #E5E7EB'
                      }}>
                        {t("dueDiligence.casesList.filters.selectStatus")}
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem'
                      }}>
                        {statusOptions.map((option) => {
                          const isSelected = selectedStatuses.includes(option.value);
                          return (
                            <label
                              key={option.value}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusToggle(option.value);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.75rem',
                                cursor: 'pointer',
                                borderRadius: '0.5rem',
                                fontSize: '0.875rem',
                                color: '#111827',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#F9FAFB';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <div style={{
                                position: 'relative',
                                width: '1.25rem',
                                height: '1.25rem',
                                flexShrink: 0
                              }}>
                                {isSelected ? (
                                  <div style={{
                                    width: '1.25rem',
                                    height: '1.25rem',
                                    borderRadius: '50%',
                                    backgroundColor: '#3B82F6',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px solid #3B82F6'
                                  }}>
                                    <FiCheck size={12} color="#ffffff" />
                                  </div>
                                ) : (
                                  <div style={{
                                    width: '1.25rem',
                                    height: '1.25rem',
                                    borderRadius: '50%',
                                    border: '2px solid #D1D5DB',
                                    backgroundColor: 'transparent'
                                  }} />
                                )}
                              </div>
                              <div style={{
                                width: '0.5rem',
                                height: '0.5rem',
                                borderRadius: '50%',
                                backgroundColor: option.color,
                                flexShrink: 0
                              }} />
                              <span style={{ flex: 1 }}>{option.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
                <div style={{ position: 'relative' }} data-filter-dropdown>
                  <FilterButton
                    $active={activeFilter === "date" || dateRange.start !== "" || dateRange.end !== ""}
                    onClick={() => handleFilterClick("date")}
                  >
                    {t("dueDiligence.casesList.filters.date")}
                    <FiCalendar size={16} style={{ marginLeft: '0.25rem' }} />
                    {(dateRange.start || dateRange.end) && (
                      <span style={{
                        marginLeft: '0.25rem',
                        fontSize: '0.75rem',
                        backgroundColor: '#021526',
                        color: '#ffffff',
                        borderRadius: '50%',
                        width: '1.25rem',
                        height: '1.25rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        ✓
                      </span>
                    )}
                  </FilterButton>
                  {showDatePicker && (
                    <div
                      data-filter-dropdown
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '0.5rem',
                        backgroundColor: '#ffffff',
                        border: '1px solid #E5E7EB',
                        borderRadius: '0.75rem',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        padding: '1.5rem',
                        zIndex: 9999,
                        minWidth: '320px'
                      }}
                    >
                      <div style={{
                        marginBottom: '1rem',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#111827'
                      }}>
                        {t("dueDiligence.casesList.filters.selectDateRange")}
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem'
                      }}>
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: '#6B7280',
                            marginBottom: '0.5rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>
                            {t("dueDiligence.casesList.filters.dateFrom")}
                          </label>
                          <input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) => handleDateRangeChange("start", e.target.value)}
                            max={dateRange.end || undefined}
                            style={{
                              width: '100%',
                              padding: '0.625rem',
                              border: '1px solid #E5E7EB',
                              borderRadius: '0.5rem',
                              fontSize: '0.875rem',
                              color: '#111827',
                              backgroundColor: '#ffffff',
                              transition: 'all 0.2s'
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#021526';
                              e.target.style.boxShadow = '0 0 0 3px rgba(2, 21, 38, 0.1)';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#E5E7EB';
                              e.target.style.boxShadow = 'none';
                            }}
                          />
                        </div>
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: '#6B7280',
                            marginBottom: '0.5rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>
                            {t("dueDiligence.casesList.filters.dateTo")}
                          </label>
                          <input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) => handleDateRangeChange("end", e.target.value)}
                            min={dateRange.start || undefined}
                            style={{
                              width: '100%',
                              padding: '0.625rem',
                              border: '1px solid #E5E7EB',
                              borderRadius: '0.5rem',
                              fontSize: '0.875rem',
                              color: '#111827',
                              backgroundColor: '#ffffff',
                              transition: 'all 0.2s'
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#021526';
                              e.target.style.boxShadow = '0 0 0 3px rgba(2, 21, 38, 0.1)';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#E5E7EB';
                              e.target.style.boxShadow = 'none';
                            }}
                          />
                        </div>
                      </div>
                      {(dateRange.start || dateRange.end) && (
                        <div style={{
                          marginTop: '1rem',
                          paddingTop: '1rem',
                          borderTop: '1px solid #E5E7EB',
                          display: 'flex',
                          gap: '0.5rem'
                        }}>
                          <button
                            onClick={clearDateRange}
                            style={{
                              flex: 1,
                              padding: '0.5rem 0.75rem',
                              backgroundColor: 'transparent',
                              border: '1px solid #E5E7EB',
                              borderRadius: '0.5rem',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              color: '#6B7280',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#F9FAFB';
                              e.currentTarget.style.borderColor = '#D1D5DB';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.borderColor = '#E5E7EB';
                            }}
                          >
                            {t("dueDiligence.casesList.filters.clear")}
                          </button>
                          <button
                            onClick={() => setShowDatePicker(false)}
                            style={{
                              flex: 1,
                              padding: '0.5rem 0.75rem',
                              backgroundColor: '#021526',
                              border: '1px solid #021526',
                              borderRadius: '0.5rem',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              color: '#ffffff',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#1F2937';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#021526';
                            }}
                          >
                            {t("dueDiligence.casesList.filters.apply")}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </FiltersContainer>
            </SearchAndFiltersWrapper>

            {filteredCases.length > 0 ? (
              <>
                <TableWrapper>
                  <Table>
                    <TableHead>
                      <TableHeadRow>
                        <TableHeaderCell
                          className="sortable"
                          onClick={() => handleSort("name")}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            {t("dueDiligence.casesList.table.columns.name")}
                            {sortField === "name" && (
                              <span className="sort-icon" style={{ fontSize: '1rem', opacity: 1 }}>
                                {sortDirection === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                            {sortField !== "name" && (
                              <span className="sort-icon" style={{ fontSize: '1rem', opacity: 0.4 }}>
                                ↕
                              </span>
                            )}
                          </div>
                        </TableHeaderCell>
                        <TableHeaderCell
                          className="sortable"
                          onClick={() => handleSort("tier")}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            {t("dueDiligence.casesList.table.columns.tier")}
                            {sortField === "tier" && (
                              <span className="sort-icon" style={{ fontSize: '1rem', opacity: 1 }}>
                                {sortDirection === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                            {sortField !== "tier" && (
                              <span className="sort-icon" style={{ fontSize: '1rem', opacity: 0.4 }}>
                                ↕
                              </span>
                            )}
                          </div>
                        </TableHeaderCell>
                        <TableHeaderCell
                          className="sortable"
                          onClick={() => handleSort("createdAt")}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            {t("dueDiligence.casesList.table.columns.createdAt")}
                            {sortField === "createdAt" && (
                              <span className="sort-icon" style={{ fontSize: '1rem', opacity: 1 }}>
                                {sortDirection === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                            {sortField !== "createdAt" && (
                              <span className="sort-icon" style={{ fontSize: '1rem', opacity: 0.4 }}>
                                ↕
                              </span>
                            )}
                          </div>
                        </TableHeaderCell>
                        <TableHeaderCell
                          className="sortable"
                          onClick={() => handleSort("status")}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            {t("dueDiligence.casesList.table.columns.status")}
                            {sortField === "status" && (
                              <span className="sort-icon" style={{ fontSize: '1rem', opacity: 1 }}>
                                {sortDirection === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                            {sortField !== "status" && (
                              <span className="sort-icon" style={{ fontSize: '1rem', opacity: 0.4 }}>
                                ↕
                              </span>
                            )}
                          </div>
                        </TableHeaderCell>
                        <TableHeaderCell className="actions"></TableHeaderCell>
                      </TableHeadRow>
                    </TableHead>
                    <TableBody>
                      {paginatedCases.map((caseItem) => (
                        <TableRow
                          key={caseItem.id}
                          onClick={() => navigate(`/due-diligence/case/${caseItem.alias}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <NameText>{caseItem.name}</NameText>
                          </TableCell>
                          <TableCell>
                            {caseItem.tier
                              ? t("dueDiligence.casesList.table.tierName", {
                                  level: DUE_DILIGENCE_TIER_LEVELS[caseItem.tier],
                                  name: t(`dueDiligence.caseDetails.tierLabels.${caseItem.tier}`),
                                })
                              : "—"}
                          </TableCell>
                          <TableCell>
                            {formatDate(caseItem.createdAt)}
                          </TableCell>
                          <TableCell>
                            <StatusBadge $status={caseItem.status}>
                              {getStatusLabel(caseItem.status)}
                            </StatusBadge>
                          </TableCell>
                          <TableCell className="actions" onClick={(e) => e.stopPropagation()}>
                            <Popup
                              options={[
                                {
                                  icon: <MdRemoveRedEye />,
                                  text: t("dueDiligence.casesList.actions.viewDetails"),
                                  onClick: () => navigate(`/due-diligence/case/${caseItem.alias}`)
                                },
                                ...(caseItem.status !== "archived" ? [{
                                  icon: <FiArchive />,
                                  text: t("dueDiligence.casesList.actions.archive"),
                                  color: "#ef4444",
                                  onClick: () => handleArchiveClick(caseItem.id, caseItem.name)
                                }] : []),
                                ...(caseItem.status === "archived" ? [{
                                  icon: <FiRefreshCw />,
                                  text: t("dueDiligence.casesList.actions.unarchive"),
                                  color: "#3B82F6",
                                  onClick: () => handleUnarchiveClick(caseItem.id, caseItem.name, !!caseItem.finalStatement, caseItem.previousStatus)
                                }] : [])
                              ]}
                            />
                          </TableCell>
                        </TableRow>
                      ))}

                    </TableBody>
                  </Table>
                </TableWrapper>

                <PaginationWrapper>
                  <PaginationInfo>
                    <Trans
                      i18nKey="dueDiligence.casesList.pagination.showingText"
                      values={{
                        start: startIndex + 1,
                        end: Math.min(endIndex, filteredCases.length),
                        total: filteredCases.length,
                      }}
                      components={{
                        1: <span className="number" />,
                        2: <span className="number" />,
                        3: <span className="number" />,
                      }}
                    />
                  </PaginationInfo>
                  <PaginationControls>
                    <RowsPerPage>
                      <span>{t("dueDiligence.casesList.pagination.rowsPerPage")}</span>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <select
                          value={rowsPerPage}
                          onChange={handleRowsPerPageChange}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </select>
                        <FiChevronDown
                          size={16}
                          style={{
                            position: 'absolute',
                            right: '0.5rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            pointerEvents: 'none',
                            color: '#6B7280'
                          }}
                        />
                      </div>
                    </RowsPerPage>
                    <PaginationButtons>
                      <button
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        <FiChevronLeft />
                      </button>
                      <button
                        className={currentPage === 1 ? "active" : ""}
                        onClick={() => handlePageChange(1)}
                      >
                        1
                      </button>
                      {totalPages > 1 && (
                        <button
                          className={currentPage === 2 ? "active" : ""}
                          onClick={() => handlePageChange(2)}
                        >
                          2
                        </button>
                      )}
                      <button
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        <FiChevronRight />
                      </button>
                    </PaginationButtons>
                  </PaginationControls>
                </PaginationWrapper>
              </>
            ) : (
              <div style={{
                padding: "3rem 2rem",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "1rem"
              }}>
                <AiOutlineFileSearch size={48} style={{ color: '#9CA3AF', opacity: 0.6 }} />
                <h3 style={{
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  color: "#111827",
                  margin: 0
                }}>
                  {t("dueDiligence.casesList.noResults.title")}
                </h3>
                <p style={{
                  fontSize: "0.875rem",
                  color: "#6B7280",
                  margin: 0,
                  maxWidth: "400px"
                }}>
                  {t("dueDiligence.casesList.noResults.description")}
                </p>
              </div>
            )}
          </>
        ) : (
          <EmptyState>
            <EmptyStateIcon>
              <AiOutlineFileSearch />
            </EmptyStateIcon>
            <EmptyStateTitle>{t("dueDiligence.casesList.emptyState.title")}</EmptyStateTitle>
            <EmptyStateDescription>
              {t("dueDiligence.casesList.emptyState.description")}
            </EmptyStateDescription>
            <Button onClick={() => setShowNewCaseModal(true)}>
              {t("dueDiligence.casesList.emptyState.createButton")}
            </Button>
          </EmptyState>
        )}
      </CasesListContent>

      <NewCaseModal
        isOpen={showNewCaseModal}
        onClose={() => {
          setShowNewCaseModal(false);
          newCaseForm.resetForm();
        }}
        formData={newCaseForm.formData}
        errors={newCaseForm.errors}
        isLoading={newCaseForm.isLoading}
        submitAttempts={newCaseForm.submitAttempts}
        onInputChange={newCaseForm.handleInputChange}
        onSubmit={newCaseForm.handleSubmit}
      />

      <Modal isOpen={showArchiveConfirmModal}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          width: '100%',
          maxWidth: '500px'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 600,
            color: '#111827',
            margin: 0
          }}>
            {t("dueDiligence.casesList.archiveConfirmModal.title")}
          </h2>
          <p style={{
            color: '#6B7280',
            lineHeight: 1.6,
            margin: 0
          }}>
            <Trans
              i18nKey="dueDiligence.casesList.archiveConfirmModal.message"
              values={{ caseName: caseToArchive?.name }}
              components={{ 1: <strong /> }}
            />
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
            <Button
              onClick={handleConfirmArchive}
              style={{
                backgroundColor: '#ef4444',
                color: '#ffffff',
                width: '100%'
              }}
            >
              {t("dueDiligence.casesList.archiveConfirmModal.confirmButton")}
            </Button>
            <p
              onClick={() => {
                setShowArchiveConfirmModal(false);
                setCaseToArchive(null);
              }}
              style={{
                textAlign: 'center',
                color: '#6B7280',
                cursor: 'pointer',
                margin: 0,
                padding: '0.5rem 0',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#111827';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#6B7280';
              }}
            >
              {t("dueDiligence.casesList.archiveConfirmModal.cancelButton")}
            </p>
          </div>
        </div>
      </Modal>

      <UnarchiveConfirmModal
        isOpen={showUnarchiveConfirmModal}
        onClose={() => {
          setShowUnarchiveConfirmModal(false);
          setCaseToUnarchive(null);
        }}
        onConfirm={handleConfirmUnarchive}
        caseName={caseToUnarchive?.name}
        previousStatus={caseToUnarchive?.previousStatus}
        hasFinalStatement={caseToUnarchive?.hasFinalStatement}
      />
    </CasesListWrapper>
  );
};
